import React from "react";
import { IoTimeOutline } from "react-icons/io5";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";

import type { CouponTemplateTranslations } from "../types";
import type { CouponTicketBackBodyProps } from "./types";

interface CouponCodeBlockProps {
  couponName: string;
  buttonColor: string | undefined;
  copyButtonLabel: string;
  copiedMessage: string;
  onCopyCode: (code: string, copiedMessage: string) => void;
}

interface CouponExpiredNoticeProps {
  formattedDate: string;
  t: CouponTemplateTranslations;
}

const CouponCodeBlock = ({
  couponName,
  buttonColor,
  copyButtonLabel,
  copiedMessage,
  onCopyCode,
}: CouponCodeBlockProps) => (
  <div
    className="coupon-template__ticket-side-body__code"
    style={{ borderColor: buttonColor }}
  >
    <Typography
      Tag="p"
      size="l"
      classname="coupon-template__ticket-side-body__code-text"
    >
      {couponName}
    </Typography>
    <Button
      label={copyButtonLabel}
      onClick={() => onCopyCode(couponName, copiedMessage)}
      type="button"
      buttonColor={buttonColor}
      className="coupon-template__ticket-side-body__code-button"
    />
  </div>
);

const CouponExpiredNotice = ({ formattedDate, t }: CouponExpiredNoticeProps) => (
  <div>
    <div className="coupon-template__ticket-side-body__expired">
      <Typography
        Tag="p"
        size="s"
        classname="coupon-template__ticket-side-body__expired-text"
      >
        <IoTimeOutline aria-hidden="true" />
        <span>
          {t("expired_on")} {formattedDate}
        </span>
      </Typography>
    </div>
    <Typography
      Tag="p"
      size="xs"
      classname="coupon-template__ticket-side-body__expired-description u-spacing-5--top"
    >
      {t("expired")}
    </Typography>
  </div>
);

export const CouponTicketBackBody = ({
  couponName,
  dateStatus,
  buttonColor,
  copyButtonLabel,
  t,
  onCopyCode,
}: CouponTicketBackBodyProps) => {
  const isExpired = dateStatus !== null && !dateStatus.isValidDate;

  if (isExpired) {
    return (
      <CouponExpiredNotice
        formattedDate={dateStatus.formattedDate}
        t={t}
      />
    );
  }

  const formattedValidDate = dateStatus?.formattedDate;
  const copiedMessage = t("copied");

  return (
    <div>
      {couponName && (
        <CouponCodeBlock
          couponName={couponName}
          buttonColor={buttonColor}
          copyButtonLabel={copyButtonLabel}
          copiedMessage={copiedMessage}
          onCopyCode={onCopyCode}
        />
      )}
      {formattedValidDate && (
        <Typography
          Tag="p"
          size="s"
          classname="coupon-template__ticket-side-body__date-description u-spacing-5--top"
        >
          <IoTimeOutline aria-hidden="true" />
          <span>
            {t("code_validation")} {formattedValidDate}
          </span>
        </Typography>
      )}
    </div>
  );
};
